import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  Presentation, 
  Plus, 
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  Play,
  Search,
  User,
  TrendingUp,
  Trophy,
  Target,
  AlertTriangle,
  X,
  Bell,
  Lightbulb,
  Sparkles,
  Loader2,
  ChevronRight,
  Medal,
  Award,
  BarChart3,
  Zap,
  Star,
  ArrowUpRight,
  Activity,
  Library
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserPortal() {
  const { token } = useParams();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReminder, setShowReminder] = useState(true);
  const [leaderboard, setLeaderboard] = useState(null);
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityFilter, setActivityFilter] = useState("this_week");
  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    fetchUserByToken();
    fetchLeaderboard();
  }, [token]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/analytics/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  const generateTopicIdeas = async () => {
    setLoadingTopics(true);
    setShowTopicDialog(true);
    try {
      const response = await axios.post(`${API}/ai/suggest-topics`, {
        industry: user?.department || "General",
        role: "Professional",
        skill_focus: "Presentation and communication skills",
        difficulty: "intermediate"
      });
      setTopicSuggestions(response.data.topics || []);
    } catch (error) {
      console.error("Failed to generate topics:", error);
      toast.error("Failed to generate topic ideas");
    } finally {
      setLoadingTopics(false);
    }
  };

  const getMedalColor = (index) => {
    switch(index) {
      case 0: return "text-[#FFD700]";
      case 1: return "text-[#C0C0C0]";
      case 2: return "text-[#CD7F32]";
      default: return "text-[#5C5C5C]";
    }
  };

  const fetchUserByToken = async () => {
    try {
      const response = await axios.get(`${API}/users/token/${token}`);
      setUser(response.data);
      const sessionsRes = await axios.get(`${API}/sessions?user_id=${response.data.id}`);
      setSessions(sessionsRes.data);
      
      // Track login with device/browser info
      trackUserAccess(response.data.id);
      
      // Fetch user activity logs
      fetchUserActivity(response.data.id);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setError("Invalid or expired link. Please contact your administrator.");
    } finally {
      setLoading(false);
    }
  };

  const trackUserAccess = async (userId) => {
    try {
      // Gather browser/device info
      const ua = navigator.userAgent;
      const browserInfo = getBrowserInfo(ua);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Try to get IP and location (client-side estimation)
      let ipData = {};
      try {
        const ipResponse = await fetch('https://ipapi.co/json/', { timeout: 3000 });
        if (ipResponse.ok) {
          const data = await ipResponse.json();
          ipData = {
            ip_address: data.ip,
            country: data.country_name,
            city: data.city,
            location: `${data.city}, ${data.country_name}`
          };
        }
      } catch (e) {
        // IP lookup failed, continue without it
      }
      
      await axios.post(`${API}/users/${userId}/track-login`, {
        ...ipData,
        user_agent: ua,
        browser: browserInfo.browser,
        os: browserInfo.os,
        device: browserInfo.device,
        timezone: timezone
      });
    } catch (error) {
      console.error("Failed to track access:", error);
    }
  };

  const getBrowserInfo = (ua) => {
    let browser = "Unknown";
    let os = "Unknown";
    let device = "Desktop";
    
    // Detect browser
    if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
    else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Edg")) browser = "Edge";
    else if (ua.includes("Opera") || ua.includes("OPR")) browser = "Opera";
    
    // Detect OS
    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac OS")) os = "macOS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
    
    // Detect device type
    if (ua.includes("Mobile") || ua.includes("Android")) device = "Mobile";
    else if (ua.includes("Tablet") || ua.includes("iPad")) device = "Tablet";
    
    return { browser, os, device };
  };

  const fetchUserActivity = async (userId, filter = "this_week") => {
    if (!userId) return;
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/activity-log?limit=50`);
      setActivityLogs(response.data || []);
    } catch (error) {
      console.error("Failed to fetch activity:", error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Filter activity logs based on date filter
  const getFilteredActivityLogs = () => {
    if (!activityLogs.length) return [];
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
    
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(thisWeekStart);
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
    
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return activityLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      
      switch (activityFilter) {
        case "today":
          return logDate >= today;
        case "yesterday":
          return logDate >= yesterday && logDate < today;
        case "this_week":
          return logDate >= thisWeekStart;
        case "last_week":
          return logDate >= lastWeekStart && logDate <= lastWeekEnd;
        case "last_30_days":
          return logDate >= thirtyDaysAgo;
        default:
          return true;
      }
    });
  };

  const filteredActivityLogs = getFilteredActivityLogs();

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const thisMonthSessions = sessions.filter(s => s.month === currentMonth);
  const hasCompletedThisMonth = thisMonthSessions.some(s => s.status === 'analyzed' || s.status === 'completed');
  const filteredSessions = sessions.filter(session =>
    session.topic_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusLabel = (status) => {
    switch (status) {
      case 'analyzed': return 'Completed';
      case 'completed': return 'Awaiting Analysis';
      case 'in_progress': return 'In Progress';
      default: return 'Ready';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'bg-emerald-500';
      case 'completed': return 'bg-blue-500';
      case 'in_progress': return 'bg-amber-500';
      default: return 'bg-slate-400';
    }
  };

  const userRank = leaderboard?.top_by_score?.findIndex(u => u.id === user?.id) + 1 || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center p-8">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Access Error</h2>
          <p className="text-slate-600">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>EVOLVE</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/u/${token}/library`}>
              <Button variant="outline" className="rounded-full">
                <Library className="w-4 h-4 mr-2" />
                Library
              </Button>
            </Link>
            <Link to={`/u/${token}/session/new`}>
              <Button className="bg-primary hover:bg-primary/90 rounded-full shadow-lg shadow-primary/20">
                <Plus className="w-4 h-4 mr-2" />
                New Session
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Compliance Alert */}
        {!hasCompletedThisMonth && showReminder && (
          <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 text-white flex items-center gap-4 shadow-lg shadow-red-500/20 animate-fade-in">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Bell className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="font-semibold">Monthly Session Reminder</p>
              <p className="text-sm text-white/90">Complete your EVOLVE session for {new Date().toLocaleDateString('en-US', { month: 'long' })} to stay compliant.</p>
            </div>
            <Link to={`/u/${token}/session/new`}>
              <Button className="bg-white text-red-600 hover:bg-white/90 rounded-full font-semibold shadow-lg">
                Start Now
              </Button>
            </Link>
            <button onClick={() => setShowReminder(false)} className="p-2 hover:bg-white/10 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-12 gap-6">
          
          {/* Hero Profile Card - Spans 8 cols */}
          <div className="col-span-12 lg:col-span-8">
            <Card className="overflow-hidden border-0 shadow-xl shadow-slate-200/50 bg-gradient-to-br from-primary via-primary to-teal-700">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  {/* Profile Picture */}
                  <div className="relative">
                    <Avatar className="w-24 h-24 border-4 border-white/20 shadow-2xl">
                      {user?.profile_picture ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}`} 
                          alt={user.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <AvatarFallback className="bg-white/20 text-white text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                          {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </div>
                  <div className="flex-1">
                    <p className="text-white/70 text-sm uppercase tracking-widest font-semibold mb-1">
                      {getGreeting()}, Welcome back!
                    </p>
                    <h1 className="text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {user?.name}
                    </h1>
                    <p className="text-white/80">
                      {user?.designation && <span>{user.designation} • </span>}
                      {user?.department}
                      {user?.location && <span> • {user.location}</span>}
                    </p>
                    {user?.tek_id && (
                      <p className="text-white/60 text-sm mt-1">TEK ID: {user.tek_id}</p>
                    )}
                    
                    <div className="flex items-center gap-6 mt-6">
                      <div className="text-center">
                        <p className="text-5xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{user?.average_score || 0}</p>
                        <p className="text-white/70 text-sm">Avg Score</p>
                      </div>
                      <div className="w-px h-16 bg-white/20" />
                      <div className="text-center">
                        <p className="text-5xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{user?.sessions_completed || 0}</p>
                        <p className="text-white/70 text-sm">Sessions</p>
                      </div>
                      <div className="w-px h-16 bg-white/20" />
                      <div className="text-center">
                        <p className="text-5xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>#{userRank || '-'}</p>
                        <p className="text-white/70 text-sm">Rank</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Score Ring */}
                  <div className="hidden md:flex flex-col items-center justify-center">
                    <div className="relative w-32 h-32">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="12" />
                        <circle 
                          cx="64" cy="64" r="56" fill="none" stroke="white" strokeWidth="12"
                          strokeDasharray={`${(user?.average_score || 0) * 3.52} 352`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">{user?.average_score || 0}%</span>
                      </div>
                    </div>
                    <p className="text-white/70 text-xs mt-2">Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Leaderboard - Spans 4 cols */}
          <div className="col-span-12 lg:col-span-4">
            <Card className="h-full border-0 shadow-xl shadow-slate-200/50 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {leaderboard?.top_by_score?.slice(0, 5).map((leaderUser, index) => (
                  <div 
                    key={leaderUser.id}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      leaderUser.id === user?.id ? 'bg-primary/10 ring-1 ring-primary/20' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      {index < 3 ? (
                        <Medal className={`w-6 h-6 ${getMedalColor(index)}`} />
                      ) : (
                        <span className="text-sm font-bold text-slate-400">#{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate text-sm">
                        {leaderUser.name}
                        {leaderUser.id === user?.id && <span className="text-primary ml-1">(You)</span>}
                      </p>
                      <p className="text-xs text-slate-500">{leaderUser.sessions_completed || 0} sessions</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary text-lg" style={{ fontFamily: 'Manrope' }}>{leaderUser.average_score || 0}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Stats Row - Full width */}
          <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Target, label: "Total Sessions", value: user?.sessions_completed || 0, color: "text-primary" },
              { icon: Award, label: "Average Score", value: user?.average_score || 0, color: "text-amber-500" },
              { icon: Star, label: "Excellent Sessions", value: sessions.filter(s => s.overall_score >= 80).length, color: "text-emerald-500" },
              { icon: TrendingUp, label: "This Month", value: thisMonthSessions.length, color: "text-blue-500" }
            ].map((stat, i) => (
              <Card key={i} className="border-0 shadow-lg shadow-slate-200/50 bg-white hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <stat.icon className={`w-8 h-8 mx-auto mb-3 ${stat.color}`} />
                  <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Sessions Grid - Spans 8 cols */}
          <div className="col-span-12 lg:col-span-8">
            <Card className="border-0 shadow-xl shadow-slate-200/50 bg-white">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Presentation className="w-5 h-5 text-primary" />
                    Session History
                  </CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search sessions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 rounded-full bg-slate-50 border-0"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredSessions.length > 0 ? (
                  <div className="grid gap-4">
                    {filteredSessions.slice(0, 6).map((session) => (
                      <div 
                        key={session.id}
                        className="group p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-all flex items-center gap-4"
                      >
                        <div className={`w-2 h-16 rounded-full ${getStatusColor(session.status)}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{session.topic_name}</p>
                          <p className="text-sm text-slate-500 truncate">{session.concept_overview}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {session.overall_score && (
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                              session.overall_score >= 90 ? 'bg-emerald-500' :
                              session.overall_score >= 75 ? 'bg-blue-500' :
                              session.overall_score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                            }`}>
                              {session.overall_score}
                            </div>
                          )}
                          <span className="text-xs px-3 py-1 rounded-full bg-slate-200 text-slate-600">
                            {getStatusLabel(session.status)}
                          </span>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {session.status === 'created' && (
                            <Link to={`/session/${session.id}/live`}>
                              <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90">
                                <Play className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                          {session.status === 'analyzed' && (
                            <>
                              <Link to={`/session/${session.id}/replay`}>
                                <Button size="sm" variant="outline" className="rounded-full">
                                  <Play className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Link to={`/session/${session.id}/feedback`}>
                                <Button size="sm" variant="outline" className="rounded-full">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-16 text-center">
                    <Presentation className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No sessions yet. Start practicing!</p>
                    <Link to={`/u/${token}/session/new`}>
                      <Button className="rounded-full bg-primary hover:bg-primary/90">
                        <Plus className="w-4 h-4 mr-2" />
                        Create First Session
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Topic Ideas + Activity Log Stacked */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            {/* AI Topic Generator - Compact Card */}
            <Card className="border-0 shadow-xl shadow-slate-200/50 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-3xl" />
              <CardContent className="p-5 relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      Need Topic Ideas?
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      AI-powered topic suggestions for your next presentation
                    </p>
                  </div>
                </div>
                
                <Dialog open={showTopicDialog} onOpenChange={setShowTopicDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={generateTopicIdeas}
                      data-testid="generate-topics-btn"
                      className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full shadow-lg shadow-amber-500/30"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Ideas
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        AI-Generated Topic Ideas
                      </DialogTitle>
                    </DialogHeader>
                    {loadingTopics ? (
                      <div className="py-12 text-center">
                        <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                        <p className="text-slate-600">Generating creative ideas...</p>
                      </div>
                    ) : (
                      <div className="space-y-4 mt-4">
                        {topicSuggestions.map((topic, index) => (
                          <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                                {index + 1}
                              </div>
                              <h4 className="font-bold text-slate-900">{topic.title}</h4>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">{topic.description}</p>
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-slate-700 mb-2">📌 Key Points:</p>
                              <ul className="space-y-1 pl-4">
                                {topic.key_points?.map((point, i) => (
                                  <li key={i} className="text-sm text-slate-600 list-disc">{point}</li>
                                ))}
                              </ul>
                            </div>
                            <div className="p-3 bg-white rounded-xl border mb-3">
                              <p className="text-xs font-semibold text-slate-700 mb-2">📋 Suggested Structure:</p>
                              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                                <span>1. Introduction & Hook</span>
                                <span>2. Problem Statement</span>
                                <span>3. Key Concepts</span>
                                <span>4. Examples/Case Study</span>
                                <span>5. Best Practices</span>
                                <span>6. Summary & CTA</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-4 text-xs text-slate-500">
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{topic.duration_minutes || 10} mins</span>
                                <span className="flex items-center gap-1"><Target className="w-3 h-3" />{Math.ceil((topic.duration_minutes || 10) / 2)} slides</span>
                              </div>
                              <span className="text-xs text-primary font-medium">{topic.importance}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                
                <div className="mt-4 pt-4 border-t border-amber-200/50">
                  <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-2">Quick Tips</p>
                  <ul className="space-y-1.5 text-xs text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      Keep it under 10 minutes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      Focus on one key message
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      Practice makes perfect
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Activity Log - Below Topic Ideas in same column */}
            <Card className="border-0 shadow-xl shadow-slate-200/50 bg-white">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Activity className="w-5 h-5 text-[#8B5CF6]" />
                    My Activity Log
                  </CardTitle>
                  <select
                    value={activityFilter}
                    onChange={(e) => {
                      setActivityFilter(e.target.value);
                      fetchUserActivity(user?.id, e.target.value);
                    }}
                    className="text-xs border rounded-full px-3 py-1.5 bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="last_week">Last Week</option>
                    <option value="last_30_days">Last 30 Days</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {loadingActivity ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                  </div>
                ) : filteredActivityLogs.length > 0 ? (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 custom-scrollbar">
                    {filteredActivityLogs.slice(0, 20).map((log, index) => (
                      <div
                        key={log.id || index}
                        className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors"
                      >
                        <div className={`p-1.5 rounded-full flex-shrink-0 ${
                          log.action_type === 'login' ? 'bg-[#2563EB]/10' :
                          log.action_type === 'session_created' ? 'bg-[#8B5CF6]/10' :
                          log.action_type === 'session_completed' ? 'bg-[#059669]/10' :
                          'bg-slate-100'
                        }`}>
                          <Activity className={`w-3 h-3 ${
                            log.action_type === 'login' ? 'text-[#2563EB]' :
                            log.action_type === 'session_created' ? 'text-[#8B5CF6]' :
                            log.action_type === 'session_completed' ? 'text-[#059669]' :
                            'text-slate-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 capitalize">
                            {log.action_type?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {log.description}
                          </p>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {formatActivityTime(log.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No activities for this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

        </div>
      </main>
    </div>
  );
}
